import { createEffect, createSignal, Show } from 'solid-js';
import { render } from './pipeline';
import { InlineEdit, type InlineRect } from './InlineEdit';

interface Props {
  doc: string;
  template: string;
  onPreviewRef?: (el: HTMLDivElement | null) => void;
  onReplaceLines: (startLine: number, endLine: number, text: string) => void;
}

interface EditingState {
  startLine: number;
  endLine: number;
  initial: string;
  rect: InlineRect;
  block: HTMLElement;
  prevVisibility: string;
}

export function Preview(props: Props) {
  const [html, setHtml] = createSignal('');
  const [editing, setEditing] = createSignal<EditingState | null>(null);
  let hostRef: HTMLDivElement | undefined;
  let seq = 0;

  createEffect(() => {
    const my = ++seq;
    const d = props.doc;
    render(d).then((out) => {
      if (my === seq) {
        // Abandon stale inline-edit on new render
        const e = editing();
        if (e) {
          try { e.block.style.visibility = e.prevVisibility; } catch { /* */ }
          setEditing(null);
        }
        setHtml(out);
      }
    });
  });

  const handleDblClick = (e: MouseEvent) => {
    if (!hostRef) return;
    const target = e.target as HTMLElement;
    const block = target.closest('[data-source-line]') as HTMLElement | null;
    if (!block || !hostRef.contains(block)) return;
    e.preventDefault();

    const startLine = parseInt(block.dataset.sourceLine || '0', 10);
    if (!startLine) return;

    let next = block.nextElementSibling as HTMLElement | null;
    while (next && !next.getAttribute('data-source-line')) {
      next = next.nextElementSibling as HTMLElement | null;
    }
    const nextStart = next ? parseInt(next.getAttribute('data-source-line') || '0', 10) : 0;
    const lines = props.doc.split('\n');
    let endLine = nextStart > 0 ? nextStart - 1 : lines.length;
    while (endLine > startLine && (lines[endLine - 1] ?? '').trim() === '') endLine--;
    const initial = lines.slice(startLine - 1, endLine).join('\n');

    const tr = block.getBoundingClientRect();
    const hr = hostRef.getBoundingClientRect();
    const rect: InlineRect = {
      top: tr.top - hr.top + hostRef.scrollTop,
      left: tr.left - hr.left + hostRef.scrollLeft,
      width: tr.width,
      height: tr.height,
    };

    const prevVisibility = block.style.visibility;
    block.style.visibility = 'hidden';
    setEditing({ startLine, endLine, initial, rect, block, prevVisibility });
  };

  const commit = (value: string) => {
    const e = editing();
    if (!e) return;
    if (value !== e.initial) {
      props.onReplaceLines(e.startLine, e.endLine, value);
    } else {
      try { e.block.style.visibility = e.prevVisibility; } catch { /* */ }
      setEditing(null);
    }
  };
  const cancel = () => {
    const e = editing();
    if (!e) return;
    try { e.block.style.visibility = e.prevVisibility; } catch { /* */ }
    setEditing(null);
  };

  return (
    <div
      ref={(el) => { hostRef = el; props.onPreviewRef?.(el); }}
      class={`preview-host template-${props.template}`}
      onDblClick={handleDblClick}
    >
      <div class="preview-body" innerHTML={html()} />
      <Show when={editing()}>
        {(e) => (
          <InlineEdit
            initial={e().initial}
            rect={e().rect}
            onCommit={commit}
            onCancel={cancel}
          />
        )}
      </Show>
    </div>
  );
}
